/**
 * @file: AiToolTile.tsx
 * @copyright: (c) 2019-2020 sichuan zhichetech co., ltd.
 */

import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { LinearGradient } from 'expo-linear-gradient';
import { memo, useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { AiSuiteHomeTool, aiColors, tint } from '../theme';

const kBorderWidth = 1;

// Every shadow prop rides with the tool's color in one style object: web
// turns each object's shadow props into its own box-shadow, so a color set
// apart from the rest would override the shadow with an empty one.
const kTileShadow = {
  shadowOpacity: 0.14,
  shadowRadius: 14,
  shadowOffset: { width: 0, height: 6 },
};
const kIconShadow = {
  shadowOpacity: 0.35,
  shadowRadius: 6,
  shadowOffset: { width: 0, height: 3 },
};

export interface AiToolBadge {
  text: string;
  /** Something new that wants a look, not just a count. */
  attention: boolean;
}

/**
 * One AI tool on the AI home: a card washed in the tool's color, its icon
 * large and faint behind the text, lifted on a shadow of the same color.
 */
export const AiToolTile = memo(
  ({
    tool,
    badge,
    onPress,
  }: {
    tool: AiSuiteHomeTool;
    badge: AiToolBadge | null;
    onPress: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const styles = useStyles();
    const Icon = tool.icon;
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={tool.title}
        onPress={onPress}
        style={({ pressed }) => [
          styles.tile,
          {
            borderColor: tint(tool.color, 0.28),
            shadowColor: tool.color,
            ...kTileShadow,
          },
          pressed && styles.pressed,
        ]}
      >
        {/* The shadow needs the tile unclipped; the wash and the watermark
            are clipped to its corners here. */}
        <View style={styles.clip} pointerEvents="none">
          <LinearGradient
            colors={[tint(tool.color, 0.16), tint(tool.color, 0.03)]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={StyleSheet.absoluteFill}
          />
          <View style={styles.watermark}>
            <Icon
              size={rpx(76)}
              color={tint(tool.color, 0.1)}
              strokeWidth={1.5}
            />
          </View>
        </View>
        <View style={styles.top}>
          <View
            style={[
              styles.icon,
              {
                backgroundColor: tool.color,
                shadowColor: tool.color,
                ...kIconShadow,
              },
            ]}
          >
            <Icon size={16} color="#fff" />
          </View>
          {badge ? (
            <View style={[styles.badge, badge.attention && styles.attention]}>
              <Typography.Text
                medium
                size={rpx(10)}
                color={badge.attention ? '#fff' : aiColors.mutedForeground}
              >
                {badge.text}
              </Typography.Text>
            </View>
          ) : null}
        </View>
        <Typography.Text
          bold
          size={rpx(15)}
          color={aiColors.foreground}
          style={{ marginTop: rpx(14) }}
        >
          {tool.title}
        </Typography.Text>
        <Typography.Text
          size={rpx(11)}
          color={aiColors.mutedForeground}
          style={{ marginTop: rpx(3) }}
        >
          {tool.subtitle}
        </Typography.Text>
      </Pressable>
    );
  },
);

function useStyles() {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        tile: {
          flexBasis: '46%',
          flexGrow: 1,
          minHeight: rpx(120),
          padding: rpx(14),
          borderRadius: rpx(16),
          borderWidth: kBorderWidth,
          backgroundColor: aiColors.card,
          elevation: 3,
        },
        pressed: { transform: [{ scale: 0.98 }] },
        clip: {
          ...StyleSheet.absoluteFillObject,
          borderRadius: rpx(16) - kBorderWidth,
          overflow: 'hidden',
        },
        watermark: {
          position: 'absolute',
          right: -rpx(14),
          bottom: -rpx(16),
          transform: [{ rotate: '-12deg' }],
        },
        top: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        },
        icon: {
          width: rpx(36),
          height: rpx(36),
          borderRadius: rpx(12),
          alignItems: 'center',
          justifyContent: 'center',
          elevation: 3,
        },
        badge: {
          paddingHorizontal: rpx(6),
          paddingVertical: rpx(2),
          borderRadius: rpx(8),
          backgroundColor: 'rgba(255,255,255,0.8)',
        },
        attention: { backgroundColor: aiColors.green },
      }),
    [rpx],
  );
}
